import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../../atoms';

enum InsightType {
  Employees = 'Employees',
  Budgets = 'Budgets',
  Products = 'Products'
}

type InsightProps = {
  title?: string;
  type?: InsightType;
  stat?: string;
  isLoading?: boolean;
};

const IconContainer = styled(Flex)`
  width: 60px;
  height: 60px;
  align-items: center;
  justify-content: center;
`;

const StatsContainer = styled.span`
  font-size: 1.8rem;
  margin-bottom: -5px;
  margin-top: -5px;
  font-weight: bold;
`;

function Insight({ type, stat, title, isLoading }: InsightProps): JSX.Element {
  function getIcon(): {
    color: string;
    background: string;
    icon: ReactNode;
  } {
    switch (type) {
      case InsightType.Products:
        return {
          background: 'success.50',
          color: 'palette_blue.100',
          icon: (
            <Icon name={IconName.QRCode} h="1.5rem" data-testid="icon-QRCode" />
          )
        };
      case InsightType.Budgets:
        return {
          color: 'blue.400',
          background: 'palette_blue.20',
          icon: (
            <Icon name={IconName.Wallet} h="1.5rem" data-testid="icon-wallet" />
          )
        };
      case InsightType.Employees:
      default:
        return {
          background: 'palette_blue.10',
          color: 'palette_blue.100',
          icon: (
            <Icon
              name={IconName.Employees}
              h="1.2rem"
              data-testid="icon-employees"
            />
          )
        };
    }
  }

  const { color: iconColor, background: iconBackground, icon } = getIcon();

  return (
    <Box
      padding="space_unit.x4"
      flex={1}
      border="1px solid"
      borderColor="gray.50"
      borderRadius="10px"
    >
      <Flex gap="space_unit.x2">
        <IconContainer
          type={type}
          p="space_unit.x2"
          borderRadius="10px"
          background={iconBackground}
          color={iconColor}
        >
          {icon}
        </IconContainer>
        <Flex justifyContent="space-between" flexDirection="column">
          <Skeleton isLoaded={!isLoading} data-testid="insight-skeleton">
            <StatsContainer data-testid="insight-stats">
              {stat ?? '-'}
            </StatsContainer>
          </Skeleton>
          <div>{title}</div>
        </Flex>
      </Flex>
    </Box>
  );
}

export { Insight, InsightType };
